// Inline moderation for the command-center feed. Each action is an async
// Inertia visit that redirects back here and refreshes only the `activity` prop
// (lib/inertia_action.js), so the rows' verdicts ("Approved by you", …) arrive
// from the server; the flash toast stays quiet and failures go to `onError`.
// Client state is only transient: a per-subject in-flight guard and a thin undo
// stack of submission ids, each undone by `unreview`.
import { router } from '@inertiajs/svelte'
import { confirm } from '../../components/confirm.js'
import { inertiaAction } from '../../lib/inertia_action.js'
import { subjectKey } from '../../lib/moderation_live.js'

export const createFeedActions = ({ onChanged, onError }) => {
  let undoStack = $state.raw([])
  let rejectTarget = $state(null)
  let busyKeys = $state({})

  const pushUndo = (id) => (undoStack = [...undoStack.filter((stacked) => stacked !== id), id])
  const dropUndo = (id) => (undoStack = undoStack.filter((stacked) => stacked !== id))

  // One request per subject at a time, so a double click can't review twice.
  const run = async (key, errorTitle, url, data) => {
    if (!key || busyKeys[key]) return false
    busyKeys = { ...busyKeys, [key]: true }
    const result = await inertiaAction(router, 'patch', url, data, { only: ['activity'] })
    const next = { ...busyKeys }
    delete next[key]
    busyKeys = next

    if (result.ok) return true
    onError(errorTitle, result.networkError ? 'Network error — try again.' : result.message)
    return false
  }

  const reviewSubmission = async (event, path, data, errorTitle) => {
    const id = event.quest_submission?.id
    if (id && (await run(subjectKey(event), errorTitle, `/quest-submissions/${id}/${path}`, data))) pushUndo(id)
  }

  const undoSubmission = async (id) => {
    if (await run(`submission:${id}`, 'Undo failed', `/quest-submissions/${id}/unreview`, {})) dropUndo(id)
  }

  const reviewSubject = (event, url, data, errorTitle) => run(subjectKey(event), errorTitle, url, data)

  const confirmed = (options) => confirm({ confirmLabel: 'Delete', variant: 'error', ...options })

  return {
    get undoStack() { return undoStack },
    get rejectTarget() { return rejectTarget },
    get busyKeys() { return busyKeys },

    // Reviews taken in the full-screen surface: follow them in the undo stack and
    // refresh the feed so its rows show the outcome.
    noteSubmissionReview: (id) => {
      pushUndo(id)
      onChanged()
    },
    noteSubmissionUnreview: (id) => {
      dropUndo(id)
      onChanged()
    },

    approveSubmission: (event) => reviewSubmission(event, 'approve', {}, 'Approve failed'),
    rejectSubmission: (event) => (rejectTarget = event),
    cancelReject: () => (rejectTarget = null),
    confirmReject: (rejection) => {
      const event = rejectTarget
      rejectTarget = null
      if (event) reviewSubmission(event, 'reject', rejection, 'Reject failed')
    },
    // Delete is the ToS-violation action; the server always records it as
    // inappropriate content.
    deleteSubmission: async (event) => {
      const ok = await confirmed({
        title: 'Delete this submission?',
        body: 'The moment is deleted and the user sees an inappropriate-content rejection.'
      })
      if (ok) reviewSubmission(event, 'delete_moment', {}, 'Delete failed')
    },
    undoLast: () => undoStack.length > 0 && undoSubmission(undoStack[undoStack.length - 1]),
    undoForEvent: (event) => undoStack.includes(event.quest_submission?.id) && undoSubmission(event.quest_submission.id),

    approveMoment: (event) => reviewSubject(event, `/moments/${event.moment.id}/review`, { decision: 'approve' }, 'Approve failed'),
    deleteMoment: async (event) => {
      const ok = await confirmed({ title: 'Delete this moment?', body: "The moment is removed for everyone. This can't be undone from here." })
      if (ok) reviewSubject(event, `/moments/${event.moment.id}/review`, { decision: 'delete' }, 'Delete failed')
    },
    approveUser: (event) => reviewSubject(event, `/user-reports/${event.user.id}/review`, { review_action: 'approve' }, 'Approve failed'),
    clearUserIcon: async (event) => {
      const ok = await confirm({
        title: "Clear this user's profile icon?",
        body: 'Their icon is removed and the reports are marked reviewed.',
        confirmLabel: 'Clear icon',
        variant: 'error'
      })
      if (ok) reviewSubject(event, `/user-reports/${event.user.id}/review`, { review_action: 'clear_icon' }, 'Clear icon failed')
    }
  }
}
